/**
 * ARTEH Design Tokens
 *
 * Centralized design system tokens for consistent styling across the application.
 * These tokens should be used throughout the app instead of hardcoded values.
 *
 * Usage:
 *   import { designTokens } from '../theme/designTokens'
 *
 *   // In styled components:
 *   padding: ${designTokens.spacing.md}
 *
 *   // In sx prop:
 *   sx={{ margin: designTokens.spacing.lg }}
 */

// Spacing scale (8px base unit)
export const spacing = {
	xs: "4px",
	sm: "8px",
	md: "16px",
	lg: "24px",
	xl: "32px",
	xxl: "48px",
	xxxl: "64px",
} as const;

// Typography scale with proper hierarchy
export const typography = {
	h1: {
		fontSize: "2.5rem", // 40px
		lineHeight: 1.2,
		fontWeight: 700,
	},
	h2: {
		fontSize: "2rem", // 32px
		lineHeight: 1.3,
		fontWeight: 700,
	},
	h3: {
		fontSize: "1.75rem", // 28px
		lineHeight: 1.4,
		fontWeight: 600,
	},
	h4: {
		fontSize: "1.5rem", // 24px
		lineHeight: 1.4,
		fontWeight: 600,
	},
	h5: {
		fontSize: "1.25rem", // 20px
		lineHeight: 1.5,
		fontWeight: 600,
	},
	h6: {
		fontSize: "1rem", // 16px
		lineHeight: 1.5,
		fontWeight: 600,
	},
	body1: {
		fontSize: "1rem", // 16px
		lineHeight: 1.5,
		fontWeight: 400,
	},
	body2: {
		fontSize: "0.875rem", // 14px
		lineHeight: 1.5,
		fontWeight: 400,
	},
	caption: {
		fontSize: "0.75rem", // 12px
		lineHeight: 1.4,
		fontWeight: 400,
	},
} as const;

// Color palette with semantic names
export const colors = {
	primary: {
		main: "#00171c",
		light: "#1a3339",
		dark: "#000000",
		contrastText: "#ffffff",
	},
	secondary: {
		main: "#7cced1",
		light: "#a8e0e3",
		dark: "#4fa6a9",
		contrastText: "#000000",
	},
	hover: "#f7d28c",
	error: "#d32f2f",
	warning: "#ed6c02",
	info: "#0288d1",
	success: "#2e7d32",
	demo: "#5DBEC3",
	background: {
		default: "#fafafa",
		paper: "#ffffff",
	},
	text: {
		primary: "rgba(0, 0, 0, 0.87)",
		secondary: "rgba(0, 0, 0, 0.6)",
		disabled: "rgba(0, 0, 0, 0.38)",
	},
} as const;

// Border radius values
export const borderRadius = {
	sm: "4px",
	md: "8px",
	lg: "12px",
	xl: "16px",
	round: "50%",
} as const;

// Shadow values for elevation
export const shadows = {
	sm: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
	md: "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
	lg: "0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)",
	xl: "0 20px 25px rgba(0, 0, 0, 0.15), 0 10px 10px rgba(0, 0, 0, 0.04)",
} as const;

// Transition timing values
export const transitions = {
	fast: "150ms ease-in-out",
	normal: "250ms ease-in-out",
	slow: "350ms ease-in-out",
} as const;

// Breakpoint values (matching MUI defaults)
export const breakpoints = {
	xs: "0px",
	sm: "600px",
	md: "900px",
	lg: "1200px",
	xl: "1536px",
} as const;

// Z-index layers for consistent stacking
export const zIndex = {
	mobileStepper: 1000,
	speedDial: 1050,
	appBar: 1100,
	drawer: 1200,
	modal: 1300,
	snackbar: 1400,
	tooltip: 1500,
} as const;

// Export all tokens as a single object
export const designTokens = {
	spacing,
	typography,
	colors,
	borderRadius,
	shadows,
	transitions,
	breakpoints,
	zIndex,
} as const;

export default designTokens;
